type BreakpointSizes = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export const breakpoints: Record<BreakpointSizes, number> = Object.freeze({
  xs: 0,
  sm: 376,
  md: 768,
  lg: 1024,
  xl: 1440
});

export const getBreakpoint = (size: BreakpointSizes) => {
  return breakpoints[`${size}`];
};

type MediaQueryUp = `@${string} (min-width: ${number}px)`;
type MediaQueryDown = `@${string} (max-width: ${number}px)`;
type MediaQueryBetween =
  `@media (min-width: ${number}px) and (max-width: ${number}px)`;

/**
 * Media query for min-width
 * @example getMediaUp('sm') => @(media|container) (min-width: 376px)
 * @param size
 * REMEMBER: Container queries require you to set `container-type: inline-size;` on the parent container
 */

export const getMediaUp = (
  size: BreakpointSizes,
  isContainer?: boolean
): MediaQueryUp => {
  return `@${isContainer ? 'container' : 'media'} (min-width: ${getBreakpoint(
    size
  )}px)`;
};

/**
 * Media query for max-width
 * @example getMediaDown('sm') => @media (max-width: 767.98px)
 * @param size
 * REMEMBER: Container queries require you to set `container-type: inline-size;` on the parent container
 */
export const getMediaDown = (
  size: Exclude<BreakpointSizes, 'xs'> | 'never',
  isContainer?: boolean
): MediaQueryDown => {
  return `@${isContainer ? 'container' : 'media'} (max-width: ${
    size == 'never' ? 0 : getBreakpoint(size) - 0.02
  }px)`;
};

/**
 * Media query for a specific breakpoint
 * @example getMediaOnly('xs') => @media (max-width: 375.98px)
 * @example getMediaOnly('md') => @media (min-width: 768px) and (max-width: 1023.98px)
 * @example getMediaOnly('xl') => @media (min-width: 1440px)
 * @param size
 */
export const getMediaOnly = (
  size: BreakpointSizes
): MediaQueryUp | MediaQueryDown | MediaQueryBetween => {
  switch (size) {
    case 'xs':
      return getMediaDown('sm');
    case 'sm':
      return getMediaBetween('sm', 'md');
    case 'md':
      return getMediaBetween('md', 'lg');
    case 'lg':
      return getMediaBetween('lg', 'xl');
    case 'xl':
      return getMediaUp('xl');
  }
};

/**
 * Media query for min-width and max-width
 * @example getMediaBetween('sm', 'lg') => @media (min-width: 376px) and (max-width: 1023.98px)
 * @param min - min-width (inclusive)
 * @param max - max-width (exclusive)
 */
export const getMediaBetween = (
  min: BreakpointSizes,
  max: Exclude<BreakpointSizes, 'xs'>
): MediaQueryBetween => {
  return `@media (min-width: ${getBreakpoint(min)}px) and (max-width: ${
    getBreakpoint(max) - 0.02
  }px)`;
};
